// src/components/GameLobby.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import socketClient from '../services/socketClient';
import useAuth from '../hooks/useAuth';
import useGameWebSocket from '../hooks/useGameWebSocket';
import PlayerName from './PlayerName';
import DeckUploadModal from './modals/DeckUploadModal';
import ErrorModal from './modals/ErrorModal';
import Modal from './shared/Modal';
import Button from './shared/Button';
import './GameLobby.css';

function GameLobby() {
  const navigate = useNavigate();
  const { requireAuth, username, token, getRoomData, clearRoomData } = useAuth();
  const [gameState, setGameState] = useState({
    players: [],
    judge: null,
    roomCode: '',
    roomCreatorPlayerId: null,
    isLoading: true,
    error: null,
  });
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [copyStatus, setCopyStatus] = useState('copy'); // 'copy', 'copying', 'copied'
  const [botCount, setBotCount] = useState(0);
  const [isRoomCreator, setIsRoomCreator] = useState(false);
  const [isDeputy, setIsDeputy] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [poopRuleEnabled, setPoopRuleEnabled] = useState(false);
  const [designatedFirstCzarId, setDesignatedFirstCzarId] = useState(null);
  const [showPoopRuleWarning, setShowPoopRuleWarning] = useState(false);
  const [coHostEnabled, setCoHostEnabled] = useState(false);

  // CUSTOM DECK UPLOAD: State for managing custom decks and modals
  const [customDecks, setCustomDecks] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [totalBlack, setTotalBlack] = useState(0);
  const [totalWhite, setTotalWhite] = useState(0);

  // STANDARD DECK SELECTION: State for managing standard decks
  const [availableStandardDecks, setAvailableStandardDecks] = useState([]);
  const [selectedStandardDecks, setSelectedStandardDecks] = useState([]);

  // MEMORY LEAK FIX: Use ref to track if component is mounted
  const mountedRef = useRef(true);

  // MEMORY LEAK FIX: Store cleanup functions
  const cleanupFunctionsRef = useRef([]);

  // MEMORY LEAK FIX: Individual event handler functions for proper cleanup
  const handleRoomUpdated = (data) => {
    if (!mountedRef.current) return;
    console.log('🏠 Room updated:', data);
    const { roomCode } = getRoomData();

    // Note: Backend sends room data without roomCode in the room-updated event
    // So we use the room code from our local state
    const players = data.players || [];
    const bots = players.filter((p) => p.isBot);
    setBotCount(bots.length);

    // Check if current user is room creator or deputy (compare player IDs)
    if (data.roomCreatorPlayerId) {
      const ourPlayer = players.find((p) => p.username === username);
      if (ourPlayer) {
        setIsRoomCreator(ourPlayer.id === data.roomCreatorPlayerId);
        setIsDeputy(ourPlayer.id === data.deputyPlayerId);
      }
    }

    // Update poop rule enabled state
    if (data.poopRuleEnabled !== undefined) {
      setPoopRuleEnabled(data.poopRuleEnabled);
    }

    // Update co-host enabled state
    if (data.coHostEnabled !== undefined) {
      setCoHostEnabled(data.coHostEnabled);
    }

    setGameState((prev) => ({
      ...prev,
      players: players,
      roomCode: data.roomCode || prev.roomCode || roomCode,
      roomCreatorPlayerId: data.roomCreatorPlayerId || prev.roomCreatorPlayerId,
      deputyPlayerId: data.deputyPlayerId || prev.deputyPlayerId,
    }));
  };

  const handleGameStarted = (gameData) => {
    if (!mountedRef.current) return;
    console.log('🎮 Game started:', gameData);

    // Edge browser needs a small delay before navigation to prevent WebSocket disconnect
    const isEdge = navigator.userAgent.toLowerCase().includes('edg');
    if (isEdge) {
      console.log('🌐 Edge detected - adding navigation delay to preserve connection');
      setTimeout(() => {
        if (mountedRef.current) {
          navigate('/game-room');
        }
      }, 100); // 100ms delay for Edge
    } else {
      navigate('/game-room');
    }
  };

  const handleNavigateToGame = (data) => {
    if (!mountedRef.current) return;
    console.log('🚀 Navigate to game:', data);

    // Edge browser needs a small delay before navigation to prevent WebSocket disconnect
    const isEdge = navigator.userAgent.toLowerCase().includes('edg');
    if (isEdge) {
      console.log('🌐 Edge detected - adding navigation delay to preserve connection');
      setTimeout(() => {
        if (mountedRef.current) {
          navigate(data.path || '/game-room');
        }
      }, 100); // 100ms delay for Edge
    } else {
      navigate(data.path || '/game-room');
    }
  };

  const handleGameStateUpdate = (newState) => {
    if (!mountedRef.current) return;
    console.log('📊 Game state updated:', newState);

    const players = newState.players || [];
    const bots = players.filter((p) => p.isBot);
    setBotCount(bots.length);

    // Check if current user is room creator (compare player IDs)
    if (newState.roomCreatorPlayerId) {
      const ourPlayer = newState.players?.find((p) => p.username === username);
      if (ourPlayer) {
        setIsRoomCreator(ourPlayer.id === newState.roomCreatorPlayerId);
      }
    }

    setGameState((prev) => ({
      ...prev,
      players: players,
      judge: newState.judge || prev.judge,
      roomCreatorPlayerId: newState.roomCreatorPlayerId || prev.roomCreatorPlayerId,
    }));
  };

  const handleBotRemovedForHuman = (data) => {
    if (!mountedRef.current) return;
    console.log('🤖➡️👤 Bot removed for human:', data);

    // Show a notification to users in the room
    setNotificationMessage(data.message);

    // Clear notification after 5 seconds
    setTimeout(() => {
      if (mountedRef.current) {
        setNotificationMessage('');
      }
    }, 5000);
  };

  const handlePlayerKicked = (data) => {
    if (!mountedRef.current) return;
    console.log('👢 Player kicked:', data);

    // Show notification and redirect to landing page
    alert(data.message || 'You have been removed from the game');
    navigate('/');
  };

  const handleFirstCzarDesignated = (data) => {
    if (!mountedRef.current) return;
    console.log('💩 First Judge designated:', data);
    setDesignatedFirstCzarId(data.playerId);
  };

  const handleFirstCzarDesignationCleared = () => {
    if (!mountedRef.current) return;
    console.log('💩 First Judge designation cleared');
    setDesignatedFirstCzarId(null);
  };

  // CUSTOM DECK UPLOAD: Handle custom decks updated event
  const handleCustomDecksUpdated = (data) => {
    if (!mountedRef.current) return;
    console.log('📁 Custom decks updated:', data);
    setCustomDecks(data.decks || []);
    setTotalBlack(data.totalBlack || 0);
    setTotalWhite(data.totalWhite || 0);
  };

  // STANDARD DECK SELECTION: Handle standard decks updated event
  const handleStandardDecksUpdated = (data) => {
    if (!mountedRef.current) return;
    console.log('📚 Standard decks updated:', data);
    setSelectedStandardDecks(data.selectedStandardDecks || []);
  };

  // CUSTOM DECK UPLOAD: Handle deck upload
  const handleUploadDeck = async (deckData) => {
    return new Promise((resolve, reject) => {
      if (!socketClient.socket) {
        setUploadErrors(['Not connected to server']);
        setShowErrorModal(true);
        reject(new Error('Not connected to server'));
        return;
      }

      console.log('📁 Uploading deck:', deckData.name);

      let timeoutId;
      let resolved = false;

      // Fallback timeout - if callback doesn't fire within 3 seconds, close modal anyway
      // The customDecksUpdated event will update the UI if successful
      timeoutId = setTimeout(() => {
        if (!resolved) {
          console.log('⏱️ Upload timeout - assuming success via broadcast');
          setShowUploadModal(false);
          resolved = true;
          resolve();
        }
      }, 3000);

      socketClient.socket.emit('uploadCustomDeck', deckData, (response) => {
        clearTimeout(timeoutId);
        if (resolved) return; // Already handled by timeout

        resolved = true;
        if (response && response.success) {
          console.log('✅ Deck uploaded successfully');
          setShowUploadModal(false);
          resolve();
        } else if (response && response.errors) {
          console.error('❌ Deck upload failed:', response.errors);
          setUploadErrors(response.errors);
          setShowErrorModal(true);
          reject(new Error(response.errors[0] || 'Upload failed'));
        } else {
          // No response or malformed response - assume success
          console.log('✅ Upload complete (no callback response)');
          setShowUploadModal(false);
          resolve();
        }
      });
    });
  };

  // CUSTOM DECK UPLOAD: Handle deck removal
  const handleRemoveDeck = (deckId) => {
    if (!socketClient.socket) {
      alert('Not connected to server');
      return;
    }

    if (window.confirm('Are you sure you want to remove this deck?')) {
      console.log('🗑️ Removing deck:', deckId);

      socketClient.socket.emit('removeCustomDeck', deckId, (response) => {
        if (response.success) {
          console.log('✅ Deck removed successfully');
        } else {
          console.error('❌ Deck removal failed:', response.error);
          alert(`Failed to remove deck: ${response.error}`);
        }
      });
    }
  };

  // STANDARD DECK SELECTION: Handle standard deck checkbox toggle
  const handleStandardDeckToggle = (deckId) => {
    if (!socketClient.socket) {
      alert('Not connected to server');
      return;
    }

    const newSelection = selectedStandardDecks.includes(deckId)
      ? selectedStandardDecks.filter((id) => id !== deckId)
      : [...selectedStandardDecks, deckId];

    console.log('📚 Updating standard deck selection:', newSelection);

    socketClient.socket.emit('updateStandardDecks', newSelection, (response) => {
      if (response && response.success) {
        console.log('✅ Standard decks updated successfully');
        // The standardDecksUpdated event will update our state
      } else if (response && response.errors) {
        console.error('❌ Standard deck update failed:', response.errors);
        alert(`Failed to update standard decks: ${response.errors.join(', ')}`);
      }
    });
  };

  const handleKickPlayer = (targetPlayerId, playerName = 'this player') => {
    const { roomCode } = getRoomData();
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot kick player: missing roomCode or socket');
      return;
    }

    // Safety check: ensure targetPlayerId is valid
    if (!targetPlayerId || typeof targetPlayerId !== 'string') {
      console.error('Cannot kick player: invalid player ID', targetPlayerId);
      alert('Error: Invalid player ID. Please refresh the page and try again.');
      return;
    }

    // Check if deputy is trying to kick room owner - show MUTINY confirmation
    const isKickingOwner = targetPlayerId === gameState.roomCreatorPlayerId;
    let confirmMessage;

    if (isKickingOwner) {
      confirmMessage = `🏴‍☠️ MUTINY!\n\nYou are the co-host trying to kick the game owner (${playerName}).\n\nAre you sure you want to remove the owner?`;
    } else {
      confirmMessage = `Are you sure you want to kick ${playerName}?`;
    }

    if (window.confirm(confirmMessage)) {
      console.log(`👢 Kicking player ${targetPlayerId} from room ${roomCode}`);
      socketClient.socket.emit('kick-player', {
        roomCode: roomCode.toUpperCase(),
        targetPlayerId,
      });
    }
  };

  const handleDesignateFirstCzar = (playerId) => {
    const { roomCode } = getRoomData();
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot designate first czar: missing roomCode or socket');
      return;
    }

    if (designatedFirstCzarId === playerId) {
      // Clear designation if clicking same player
      console.log(`💩 Clearing first Judge designation from room ${roomCode}`);
      socketClient.socket.emit('clear-first-czar-designation', {
        roomCode: roomCode.toUpperCase(),
      });
    } else {
      // Designate player as first Judge
      console.log(`💩 Designating player ${playerId} as first Judge in room ${roomCode}`);
      socketClient.socket.emit('designate-first-czar', {
        roomCode: roomCode.toUpperCase(),
        playerId,
      });
    }
  };

  // CO-HOST SYSTEM: Toggle co-host feature
  const handleToggleCoHost = () => {
    const { roomCode } = getRoomData();
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot toggle co-host: missing roomCode or socket');
      return;
    }

    const newEnabled = !coHostEnabled;
    console.log(`🎖️ ${newEnabled ? 'Enabling' : 'Disabling'} co-host feature in room ${roomCode}`);
    socketClient.socket.emit('toggle-cohost', {
      roomCode: roomCode.toUpperCase(),
      enabled: newEnabled,
    });
  };

  // CO-HOST SYSTEM: Designate deputy/co-host
  const handleDesignateDeputy = (playerId) => {
    const { roomCode } = getRoomData();
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot designate deputy: missing roomCode or socket');
      return;
    }

    if (gameState.deputyPlayerId === playerId) {
      // Clear designation if clicking same player - just toggle co-host off and on
      console.log(`🎖️ Clearing deputy designation from room ${roomCode}`);
      socketClient.socket.emit('toggle-cohost', {
        roomCode: roomCode.toUpperCase(),
        enabled: false,
      });
      // Re-enable after clearing
      setTimeout(() => {
        socketClient.socket.emit('toggle-cohost', {
          roomCode: roomCode.toUpperCase(),
          enabled: true,
        });
      }, 100);
    } else {
      // Designate player as deputy
      console.log(`🎖️ Designating player ${playerId} as deputy in room ${roomCode}`);
      socketClient.socket.emit('designate-deputy', {
        roomCode: roomCode.toUpperCase(),
        playerId,
      });
    }
  };

  const handleSocketConnect = () => {
    if (!mountedRef.current) return;
    console.log('✅ Socket connected in GameLobby');
    setConnectionStatus('connected');
  };

  const handleSocketConnectError = (error) => {
    if (!mountedRef.current) return;
    console.error('❌ Socket connection error in GameLobby:', error);
    setConnectionStatus('error');
    setGameState((prev) => ({
      ...prev,
      isLoading: false,
      error: error.message || 'Failed to connect to game',
    }));
  };

  // MEMORY LEAK FIX: Main useEffect with proper cleanup
  useEffect(() => {
    // Mark component as mounted
    mountedRef.current = true;

    // Check auth first
    if (!requireAuth()) return;

    // Get room data using the hook
    const { roomCode } = getRoomData();

    if (!roomCode) {
      console.error('Missing room data, redirecting to landing page');
      navigate('/');
      return;
    }

    const initializeConnection = async () => {
      try {
        // Connect to WebSocket server
        setConnectionStatus('connecting');
        socketClient.connect();

        // MEMORY LEAK FIX: Promise with proper cleanup
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            // Clean up temporary listeners
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            reject(new Error('Connection timeout'));
          }, 10000);

          // Store cleanup function
          cleanupFunctionsRef.current.push(() => clearTimeout(timeout));

          if (socketClient.isSocketConnected()) {
            clearTimeout(timeout);
            resolve();
            return;
          }

          const tempConnectHandler = () => {
            clearTimeout(timeout);
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            resolve();
          };

          const tempErrorHandler = (error) => {
            clearTimeout(timeout);
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            reject(error);
          };

          socketClient.on('connect', tempConnectHandler);
          socketClient.on('connect_error', tempErrorHandler);
        });

        // Check if component is still mounted
        if (!mountedRef.current) return;

        setConnectionStatus('connected');

        // FIX #247: Only join if not already in this room (prevents double-join from JoinGame -> GameLobby navigation)
        let joinResult = null;
        if (!socketClient.isAlreadyInRoom(roomCode)) {
          console.log('🚪 Joining room (not already joined):', roomCode);
          joinResult = await socketClient.joinRoom(roomCode, username, token);
        } else {
          console.log('🚪 Already in room, skipping redundant join:', roomCode);
        }

        if (!mountedRef.current) return;

        // Set initial game state
        setGameState((prev) => ({
          ...prev,
          roomCode: roomCode.toUpperCase(),
          players: joinResult?.players || prev.players || [],
          isLoading: false,
          error: null,
        }));

        // FIXED: Get current game state with roomCode parameter
        try {
          const currentState = await socketClient.getGameState(roomCode);

          if (!mountedRef.current) return;

          // The room may have already started by the time this resolves (e.g. this
          // mount raced a 'game-started' broadcast that fired before useGameWebSocket's
          // listeners were registered, or before this component mounted at all after
          // a reconnect). handleGameStarted/handleNavigateToGame only fire on live
          // events - without this check, a client that missed that one-time event has
          // no future trigger and is stuck on the lobby screen forever.
          if (currentState.gameStarted) {
            console.log('🎮 Game already in progress on lobby mount - navigating to game room');
            navigate('/game-room');
            return;
          }

          const players = currentState.players || [];
          const bots = players.filter((p) => p.isBot);
          setBotCount(bots.length);

          // Check if current user is room creator (compare player IDs)
          if (currentState.roomCreatorPlayerId) {
            // Get our player ID from the response or from the current player list
            const ourPlayer = currentState.players?.find((p) => p.username === username);
            if (ourPlayer) {
              setIsRoomCreator(ourPlayer.id === currentState.roomCreatorPlayerId);
            }
          }

          setGameState((prev) => ({
            ...prev,
            players: players,
            judge: currentState.judge || null,
            roomCreatorPlayerId: currentState.roomCreatorPlayerId || null,
          }));
        } catch (stateError) {
          console.warn('Could not get initial game state:', stateError);
          // This is okay, we'll get updates from room-updated events
        }

        // NOTE: Standard deck fetching moved to separate useEffect below for better reliability
      } catch (error) {
        console.error('Failed to initialize connection:', error);
        if (!mountedRef.current) return;

        setConnectionStatus('error');
        setGameState((prev) => ({
          ...prev,
          isLoading: false,
          error: error.message || 'Failed to connect to game',
        }));
      }
    };

    initializeConnection();

    // Event listeners are now set up automatically by useGameWebSocket hook

    // MEMORY LEAK FIX: Cleanup function
    return () => {
      console.log('🧹 GameLobby component unmounting - cleaning up');
      mountedRef.current = false;

      // Event listeners are now cleaned up automatically by useGameWebSocket hook

      // Clear any timeouts stored in cleanup functions
      cleanupFunctionsRef.current.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      cleanupFunctionsRef.current = [];
    };
  }, [navigate, requireAuth, username, token, getRoomData]);

  // STANDARD DECK SELECTION: Separate useEffect to fetch available standard decks
  useEffect(() => {
    // Only fetch if we have a connected socket
    if (connectionStatus !== 'connected' || !socketClient.socket || !mountedRef.current) {
      console.log('⏸️ Skipping standard decks fetch:', {
        connectionStatus,
        hasSocket: !!socketClient.socket,
        mounted: mountedRef.current,
      });
      return;
    }

    console.log('🔍 Fetching available standard decks (from separate useEffect)...');

    socketClient.socket.emit('getAvailableStandardDecks', (response) => {
      console.log('📥 Received standard decks response:', response);

      if (!mountedRef.current) {
        console.log('⚠️ Component unmounted, ignoring response');
        return;
      }

      if (response && response.success) {
        console.log('✅ Setting available standard decks:', response.decks);
        setAvailableStandardDecks(response.decks || []);

        // Initialize selected decks with defaults if not already set
        if (response.defaultDeckIds && response.defaultDeckIds.length > 0) {
          console.log('✅ Setting default deck selection:', response.defaultDeckIds);
          setSelectedStandardDecks(response.defaultDeckIds);
        }
      } else {
        console.warn('❌ Failed to fetch standard decks:', response);
      }
    });
  }, [connectionStatus]); // Re-run when connection status changes

  // WebSocket Event Handlers Object for useGameWebSocket hook
  const { roomCode } = getRoomData();
  const eventHandlers = {
    'room-updated': handleRoomUpdated,
    'joined-room': handleRoomUpdated,
    'game-started': handleGameStarted,
    'navigate-to-game': handleNavigateToGame,
    'game-state': handleGameStateUpdate,
    connect: handleSocketConnect,
    connect_error: handleSocketConnectError,
    'bot-removed-for-human': handleBotRemovedForHuman,
    'player-kicked': handlePlayerKicked,
    'first-czar-designated': handleFirstCzarDesignated,
    'first-czar-designation-cleared': handleFirstCzarDesignationCleared,
    customDecksUpdated: handleCustomDecksUpdated,
    standardDecksUpdated: handleStandardDecksUpdated,
  };

  // Use the WebSocket hook for event management
  const { emit: socketEmit } = useGameWebSocket(roomCode, eventHandlers, [username]);

  // FIXED: Use socketClient.startGame method instead of just navigating
  const handleStartGame = async () => {
    if (gameState.players.length < 2) {
      alert('Need at least 2 more players to start the game!');
      return;
    }

    // Check if poop rule is enabled but no one is designated
    if (poopRuleEnabled && !designatedFirstCzarId) {
      setShowPoopRuleWarning(true);
      return;
    }

    try {
      console.log('🎮 Starting game for room:', gameState.roomCode);
      await socketClient.startGame(gameState.roomCode);

      // Navigate to game room (this might also be triggered by navigate-to-game event)
      // State will be fetched fresh via socket in GameRoom component
      navigate('/game-room');
    } catch (error) {
      console.error('Failed to start game:', error);
      alert('Failed to start game: ' + error.message);
    }
  };

  // Handle proceeding without designation
  const handleProceedWithoutDesignation = async () => {
    setShowPoopRuleWarning(false);

    try {
      console.log('🎮 Starting game for room (without designation):', gameState.roomCode);
      await socketClient.startGame(gameState.roomCode);

      // Navigate to game room (state will be fetched fresh via socket)
      navigate('/game-room');
    } catch (error) {
      console.error('Failed to start game:', error);
      alert('Failed to start game: ' + error.message);
    }
  };

  // Handle canceling start
  const handleCancelStart = () => {
    setShowPoopRuleWarning(false);
  };

  const handleAddBot = async () => {
    try {
      await socketEmit('add-bot', { roomCode: gameState.roomCode });
    } catch (error) {
      console.error('Failed to add bot:', error);
      alert(error.message);
    }
  };

  const handleLeaveRoom = () => {
    // Event listeners are now cleaned up automatically by useGameWebSocket hook

    // Disconnect from socket
    socketClient.disconnect();

    // Clear room data
    clearRoomData();

    // Navigate back to landing page
    navigate('/');
  };

  // ENHANCED: Better copy functionality with visual feedback
  const handleCopyRoomCode = async () => {
    if (copyStatus !== 'copy') return; // Prevent multiple clicks

    setCopyStatus('copying');

    try {
      await navigator.clipboard.writeText(gameState.roomCode);
      console.log('Room code copied to clipboard');
      setCopyStatus('copied');

      // Reset after 2 seconds
      setTimeout(() => {
        if (mountedRef.current) {
          setCopyStatus('copy');
        }
      }, 2000);
    } catch (err) {
      console.error('Failed to copy room code:', err);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = gameState.roomCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        setCopyStatus('copied');
        setTimeout(() => {
          if (mountedRef.current) {
            setCopyStatus('copy');
          }
        }, 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        setCopyStatus('copy');
      }
    }
  };

  // Helper function to get copy button content
  const getCopyButtonContent = () => {
    switch (copyStatus) {
      case 'copying':
        return '⏳';
      case 'copied':
        return '✓';
      default:
        return 'Copy';
    }
  };

  // Helper function to get copy button title
  const getCopyButtonTitle = () => {
    switch (copyStatus) {
      case 'copying':
        return 'Copying...';
      case 'copied':
        return 'Copied to clipboard!';
      default:
        return 'Copy room code to clipboard';
    }
  };

  // Show loading state
  if (gameState.isLoading) {
    return (
      <div className="game-lobby">
        <div className="lobby-container">
          <div className="connection-status">
            <h2>Connecting to Game...</h2>
            <div className="loading-spinner"></div>
            <p>Status: {connectionStatus}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (gameState.error) {
    return (
      <div className="game-lobby">
        <div className="lobby-container">
          <div className="error-state">
            <h2>Connection Failed</h2>
            <p>{gameState.error}</p>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
              className="retry-button"
              ariaLabel="Retry connection"
            >
              Retry Connection
            </Button>
            <Button
              variant="secondary"
              onClick={handleLeaveRoom}
              className="leave-button"
              ariaLabel="Back to room entry"
            >
              Back to Room Entry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // #79: shared between the top of the actions column and a second copy
  // below the deck lists, so room creators don't have to scroll all the way
  // back up to start after browsing decks.
  const startGameButtonEl =
    gameState.players.filter((p) => !p.isSpectator).length >= 2 ? (
      <Button
        variant="primary"
        onClick={handleStartGame}
        className="start-game-button"
        ariaLabel="Start game"
      >
        Start Game ({gameState.players.filter((p) => !p.isSpectator).length} players)
      </Button>
    ) : (
      <Button variant="primary" disabled className="start-game-button disabled">
        Need at least 2 players
      </Button>
    );

  return (
    <div className="game-lobby">
      <div className="lobby-container">
        <div className="lobby-header">
          <div className="header-top">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearRoomData();
                socketClient.disconnect();
                navigate('/play');
              }}
              className="back-button"
              ariaLabel="Leave lobby and return to menu"
            >
              ←
            </Button>
            <h1>Game Lobby</h1>
            <div></div>
          </div>
          <div className="user-display">
            <span className="playing-as">
              Playing as: <strong>{username}</strong>
            </span>
          </div>
          <div className="room-info">
            <div className="room-code-display">
              <span>
                Room Code: <strong>{gameState.roomCode}</strong>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyRoomCode}
                className={`copy-button ${copyStatus}`}
                ariaLabel={getCopyButtonTitle()}
                disabled={copyStatus === 'copying'}
              >
                {getCopyButtonContent()}
              </Button>
            </div>
            <div className="connection-indicator">
              <span className={`status-dot ${connectionStatus}`}></span>
              <span>
                {connectionStatus === 'connected'
                  ? 'Connected'
                  : connectionStatus === 'connecting'
                    ? 'Connecting...'
                    : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>

        {/* Notification for bot removal */}
        {notificationMessage && (
          <div className="notification-banner">
            <p>{notificationMessage}</p>
          </div>
        )}

        <div className="lobby-content">
          {/* Left Column: Players + Voyeurs */}
          <div className="players-column">
            {/* Active Players Section */}
            <div className="players-section">
              <h2>Players ({gameState.players.filter((p) => !p.isSpectator).length})</h2>
              <div className="players-list">
                {gameState.players
                  .filter((player) => !player.isSpectator)
                  .map((player, index) => (
                    <div key={player.id || index} className="player-card">
                      <div className="player-info">
                        <PlayerName
                          player={player}
                          roomCreatorPlayerId={gameState.roomCreatorPlayerId}
                          deputyPlayerId={gameState.deputyPlayerId}
                          isJudge={(player.username || player.name) === gameState.judge?.username}
                          showCzar={false}
                        />
                        {/* In lobby, show "Game Owner" instead of "Judge" for room creator */}
                        {(player.id || player.playerId) === gameState.roomCreatorPlayerId && (
                          <span className="owner-badge">⭐ Game Owner</span>
                        )}
                        {gameState.deputyPlayerId &&
                          (player.id || player.playerId) === gameState.deputyPlayerId && (
                            <span className="deputy-badge">🎖️ Co-Host</span>
                          )}
                      </div>
                      <div className="player-status">
                        <span
                          className={`status-indicator ${player.connected !== false ? 'online' : 'offline'}`}
                        >
                          {player.connected !== false ? '🟢' : '🔴'}
                        </span>
                        {poopRuleEnabled &&
                          (isRoomCreator || isDeputy) &&
                          !player.isBot &&
                          !player.isSpectator && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`designate-czar-btn ${designatedFirstCzarId === (player.id || player.playerId) ? 'designated' : ''}`}
                              onClick={() => handleDesignateFirstCzar(player.id || player.playerId)}
                              ariaLabel="Designate as first Judge"
                            >
                              💩{' '}
                              {designatedFirstCzarId === (player.id || player.playerId)
                                ? 'First!'
                                : 'First'}
                            </Button>
                          )}
                        {coHostEnabled &&
                          isRoomCreator &&
                          !player.isBot &&
                          !player.isSpectator &&
                          player.id !== gameState.roomCreatorPlayerId &&
                          player.playerId !== gameState.roomCreatorPlayerId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`designate-czar-btn ${(player.id || player.playerId) === gameState.deputyPlayerId ? 'designated' : ''}`}
                              onClick={() => handleDesignateDeputy(player.id || player.playerId)}
                              ariaLabel="Designate as Co-Host"
                            >
                              🎖️{' '}
                              {(player.id || player.playerId) === gameState.deputyPlayerId
                                ? 'Co-Host!'
                                : 'Co-Host'}
                            </Button>
                          )}
                        {(isRoomCreator || isDeputy) &&
                          !player.isBot &&
                          socketClient.socket &&
                          player.socketId !== socketClient.socket.id &&
                          (player.id || player.playerId) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="kick-button"
                              onClick={() =>
                                handleKickPlayer(player.id || player.playerId, player.username)
                              }
                              ariaLabel="Kick player"
                            >
                              ✖
                            </Button>
                          )}
                      </div>
                    </div>
                  ))}
              </div>

              {gameState.players.filter((p) => !p.isSpectator).length < 2 && (
                <div className="waiting-message">
                  <p>Waiting for more players...</p>
                  <p>Share the room code with friends to invite them!</p>
                </div>
              )}
            </div>

            {/* Voyeurs Section - Only show if there are voyeurs */}
            {gameState.players.some((p) => p.isSpectator) && (
              <div className="spectators-section">
                <h2>👁️ Voyeurs ({gameState.players.filter((p) => p.isSpectator).length})</h2>
                <div className="players-list">
                  {gameState.players
                    .filter((player) => player.isSpectator)
                    .map((player, index) => (
                      <div key={player.id || index} className="player-card spectator-card">
                        <div className="player-info">
                          <PlayerName
                            player={player}
                            roomCreatorPlayerId={gameState.roomCreatorPlayerId}
                            deputyPlayerId={gameState.deputyPlayerId}
                            isJudge={false}
                            showCzar={false}
                          />
                        </div>
                        <div className="player-status">
                          <span
                            className={`status-indicator ${player.connected !== false ? 'online' : 'offline'}`}
                          >
                            {player.connected !== false ? '🟢' : '🔴'}
                          </span>
                          {(isRoomCreator || isDeputy) &&
                            socketClient.socket &&
                            player.socketId !== socketClient.socket.id &&
                            (player.id || player.playerId) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="kick-button"
                                onClick={() =>
                                  handleKickPlayer(player.id || player.playerId, player.username)
                                }
                                ariaLabel="Kick voyeur"
                              >
                                ✖
                              </Button>
                            )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          {/* End Left Column */}

          {/* Right Column: Actions + Decks */}
          <div className="actions-column">
            <div className="lobby-actions">
              {/* Only show start button to room creator */}
              {isRoomCreator ? (
                startGameButtonEl
              ) : (
                <div className="waiting-for-czar">
                  <p>Waiting for game owner to start the game...</p>
                  {gameState.players.find((p) => p.id === gameState.roomCreatorPlayerId) && (
                    <p>
                      <strong>
                        {
                          gameState.players.find((p) => p.id === gameState.roomCreatorPlayerId)
                            ?.username
                        }
                      </strong>{' '}
                      ⭐ will start when ready
                    </p>
                  )}
                </div>
              )}

              {(isRoomCreator || isDeputy) && (
                <Button
                  variant="secondary"
                  onClick={handleAddBot}
                  className="add-bot-button"
                  disabled={
                    botCount >= 4 ||
                    gameState.players.filter((p) => !p.isSpectator).length >= gameState.maxPlayers
                  }
                  ariaLabel="Add bot player"
                >
                  Add Bot ({botCount}/4)
                </Button>
              )}

              {isRoomCreator && (
                <div className="cohost-toggle-container">
                  <Button
                    variant="secondary"
                    onClick={handleToggleCoHost}
                    className={`toggle-cohost-button ${coHostEnabled ? 'enabled' : ''}`}
                    ariaLabel={coHostEnabled ? 'Disable Co-Host feature' : 'Enable Co-Host feature'}
                  >
                    🎖️ Co-Host: {coHostEnabled ? 'ON' : 'OFF'}
                  </Button>
                  <span
                    className="info-icon"
                    data-tooltip="Co-Host is a backup room manager with kick/bot powers. If you leave, they become Game Owner."
                  >
                    ℹ️
                  </span>
                </div>
              )}

              <Button
                variant="secondary"
                onClick={handleLeaveRoom}
                className="leave-room-button"
                ariaLabel="Leave room"
              >
                Leave Room
              </Button>

              {/* STANDARD DECK SELECTION: Standard Decks Section */}
              {(isRoomCreator || isDeputy) && availableStandardDecks.length > 0 && (
                <div className="standard-decks-section">
                  <div className="section-header">
                    <h2>Standard Decks</h2>
                    <span className="section-subtitle">Select built-in decks to use</span>
                  </div>

                  <div className="decks-list">
                    {availableStandardDecks.map((deck) => (
                      <div key={deck.id} className="deck-card standard-deck-card">
                        <label className="deck-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedStandardDecks.includes(deck.id)}
                            onChange={() => handleStandardDeckToggle(deck.id)}
                            className="deck-checkbox"
                          />
                          <div className="deck-info">
                            <div className="deck-name">
                              {deck.displayName}
                              {deck.isNsfw && <span className="nsfw-badge">NSFW</span>}
                              {deck.isDefault && <span className="default-badge">Default</span>}
                            </div>
                            {deck.description && (
                              <div className="deck-description">{deck.description}</div>
                            )}
                            <div className="deck-stats">
                              <span>⚫ {deck.cardCount.black} black</span>
                              <span>⚪ {deck.cardCount.white} white</span>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CUSTOM DECK UPLOAD: Custom Decks Section */}
              {(isRoomCreator || isDeputy) && (
                <div className="custom-decks-section">
                  <div className="section-header">
                    <h2>Custom Decks</h2>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowUploadModal(true)}
                      className="upload-deck-button"
                      ariaLabel="Upload custom card deck"
                    >
                      📁 Upload Deck
                    </Button>
                  </div>

                  {customDecks.length > 0 ? (
                    <>
                      <div className="decks-list">
                        {customDecks.map((deck) => (
                          <div key={deck.id} className="deck-card">
                            <div className="deck-info">
                              <div className="deck-name">{deck.name}</div>
                              {deck.description && (
                                <div className="deck-description">{deck.description}</div>
                              )}
                              <div className="deck-stats">
                                <span>⚫ {deck.blackCards.length} black</span>
                                <span>⚪ {deck.whiteCards.length} white</span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveDeck(deck.id)}
                              className="remove-deck-button"
                              ariaLabel="Remove deck"
                            >
                              ✖
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="deck-totals">
                        <span>
                          <strong>Total:</strong> {totalBlack} question cards, {totalWhite} answer
                          cards
                        </span>
                        <span className="limit-note">(max 500 question, 2000 answer)</span>
                      </div>
                    </>
                  ) : (
                    <div className="no-decks-message">
                      <p>No custom decks uploaded</p>
                      <p className="hint">
                        Want to create your own cards? Use the{' '}
                        <Link to="/deck-builder" className="deck-builder-link">
                          Deck Builder
                        </Link>{' '}
                        to design custom decks, then upload the JSON file here!
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* #79: repeats the start button below the deck lists, so it's
                  reachable without scrolling back up past a long deck list */}
              {isRoomCreator && <div className="start-game-bottom-cta">{startGameButtonEl}</div>}
            </div>
          </div>
          {/* End Right Column */}
        </div>

        <div className="lobby-footer">
          <p>Real-time multiplayer powered by WebSocket</p>
          <p>Players will see updates automatically when others join or leave</p>
        </div>

        {/* Poop Rule Warning Modal */}
        <Modal
          show={showPoopRuleWarning}
          onClose={handleCancelStart}
          title="⚠️ Poop Rule Reminder"
          footer={
            <>
              <Button variant="secondary" onClick={handleCancelStart}>
                ← Go Back
              </Button>
              <Button variant="primary" onClick={handleProceedWithoutDesignation}>
                Start Anyway →
              </Button>
            </>
          }
          size="md"
          contentClassName="poop-warning-modal"
        >
          <p>You enabled the Poop Rule but haven't designated the first Judge.</p>
          <p className="warning-subtext">Start anyway? (Room owner will go first by default)</p>
        </Modal>

        {/* CUSTOM DECK UPLOAD: Upload Modal */}
        <DeckUploadModal
          show={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadDeck}
        />

        {/* CUSTOM DECK UPLOAD: Error Modal */}
        <ErrorModal
          show={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="Deck Upload Failed"
          errors={uploadErrors}
        />
      </div>
    </div>
  );
}

export default GameLobby;
